import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import { type CarouselSlideProps } from '../ChallengeCarousel/ChallengeCarouselSlide';
import { PlayPauseButton } from './PlayPauseButton';
import { CarouselContainer, CarouselSlides, Dot, DotsContainer, NavButton } from './Carousel.styled';

interface CarouselProps {
  slides: JSX.Element[];
}

export const Carousel = ({ slides }: CarouselProps) => {
  const intl = useIntl();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
    return () => {};
  }, [isPlaying, nextSlide]);

  return (
    <div id="carousel-container">
      <CarouselContainer
        data-testid="carousel-container"
        className={isKeyboardFocused ? 'keyboard-focused' : ''}
        onMouseEnter={() => setIsPlaying(false)}
        onMouseLeave={() => setIsPlaying(true)}
        onFocus={() => {
          setIsPlaying(false);
          setIsKeyboardFocused(true);
        }}
        onBlur={() => {
          setIsKeyboardFocused(false);
          setIsPlaying(true);
        }}
      >
        <CarouselSlides data-testid="carousel-slides" currentSlide={currentSlide}>
          {slides.map((slide, index) =>
            React.isValidElement<Partial<CarouselSlideProps>>(slide) && typeof slide.type !== 'string'
              ? React.cloneElement(slide, { isActive: index === currentSlide })
              : slide
          )}
        </CarouselSlides>
        <NavButton
          aria-label={intl.formatMessage({ id: 'previousSlide', defaultMessage: 'Previous Slide' })}
          data-testid="carousel-left-nav-arrow"
          left
          onClick={prevSlide}
        >
          {String.fromCodePoint(10_094)}
        </NavButton>
        <NavButton
          aria-label={intl.formatMessage({ id: 'nextSlide', defaultMessage: 'Next Slide' })}
          data-testid="carousel-right-nav-arrow"
          onClick={nextSlide}
        >
          {String.fromCodePoint(10_095)}
        </NavButton>
      </CarouselContainer>
      <DotsContainer data-testid="carousel-dot-container">
        <PlayPauseButton isPlaying={isPlaying} togglePlayPause={togglePlayPause} />
        {slides.map((_, index) => (
          <Dot
            key={_.key}
            active={currentSlide === index}
            onClick={() => setCurrentSlide(index)}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </DotsContainer>
    </div>
  );
};
